import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import DataTable from "react-data-table-component";
import toast from "../../components/sweetalert/Sweetalert";
import Swal from "sweetalert2";
import NDOTLoader from "../../components/loader/NDOTLoader";

const NDOT = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const uname = sessionStorage.getItem('uname') || '';

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        requesttype: '',
        status: ''
    });

    const ndotDataKey = ['ndot', currentPage, rowPerPage];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    }

    const resetForm = () => {
        setFormData({
            id: 0,
            requesttype: '',
            status: ''
        });
    }

    const tableColumns = [
        { name: 'Request Type', selector: row => row.requesttype },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const deleteNDOTMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`settings/ndot/${id}?uname=${uname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(ndotDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.response || 'Something went wrong while deleting NDOT Request Type!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const deleteNDOT = (id, ndot) => {
        Swal.fire({
            text: `Are you sure you want to delete NDOT details of ${ndot}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: (result) => {
				return new Promise(function(resolve) {
                    if (result) {
                        deleteNDOTMutation.mutate(id);
                        resolve();
                    }
                });
            }
        });
    };

    const { data: ndot, isLoading: isLoadingNDOT, error: errorNDOT } = useQuery({
        queryKey: ndotDataKey,
        queryFn: async () => {
            const res = await axios.get(`settings/ndot?page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.ndot?.map(item => ({
                requesttype: item.requesttype,
                status: item.status === 1 ? <span className="label label-success">Active</span> : <span className="label label-danger">Inactive</span>,
                action: (
                    <div className="buttons">
                        <button type="button" className="btn-edit" onClick={() => setFormData({ id: item.id, requesttype: item.requesttype, status: item.status })}>
                            <i className="fa fa-edit"></i>
                        </button>
                        <button type="button" className="btn-delete" onClick={() => deleteNDOT(item.id, item.requesttype)}>
                            <i className="fa fa-trash"></i>
                        </button>
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows }
        },
        enabled: !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorNDOT) {
            if (errorNDOT.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    const submitNDOTMutation = useMutation({
        mutationFn: async (ndotData) => {
            const res = await axios.post('settings/ndot', ndotData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            queryClient.invalidateQueries(ndotDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while saving NDOT details!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitNDOT = () => {
        const { requesttype, status } = formData;

        if (!requesttype || !status) {
            toast.fire({ icon: 'error', title: "All fields are required." });
            return;
        }

        setFormSubmit(true);
        submitNDOTMutation.mutate({ ...formData, uname });
    };

    return (
        <div className="ndot">
            <div className="page-header">
                <div className="title">
                    <h1>NDOT</h1>
                    <ul className="breadcrumbs">
                        <li>
                            <NavLink to="/settings/devices">
                                Settings
                            </NavLink>
                        </li>
                        <li>
                            <span>NDOT</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className='left-panel'>
                    <DataTable 
                        columns={tableColumns} 
                        data={ndot?.data || []} 
                        progressPending={isLoadingNDOT} 
                        progressComponent={<NDOTLoader />} 
                        pagination 
                        paginationServer 
                        paginationTotalRows={ndot?.totalRows || 0} 
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        dense 
                        highlightOnHover 
                        responsive 
                    />
                </div>

                <div className='right-panel'>
                    <form>
                        <div className='form-group'>
                            <label>Request Type</label>
                            <input type="text" name="requesttype" value={formData.requesttype} onChange={handleInputChange} disabled={formSubmit} autoComplete="off" />
                        </div>
                        <div className='form-group'>
                            <label>Status</label>
                            <select name='status' value={formData.status} onChange={handleInputChange} disabled={formSubmit}>
                                <option value=''></option>
                                <option value='1'>Active</option>
                                <option value='2'>Inactive</option>
                            </select>
                        </div>
                        <div className='button-group'>
                            <button type='button' onClick={handleSubmitNDOT} disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default NDOT;